import React, { useEffect, useRef, useState, useMemo } from "react";
import { ChevronDown, Building2, Search, Check } from "lucide-react";
import { useSelectedCompany } from "../contexts/SelectedCompanyProvider";
import { formatCompany } from "../utils/companyScope";

export default function CompanySwitcher() {
  const {
    showCompanySwitcher,
    showCompanyLabel,
    companyId,
    setCompanyId,
    company,
    companies,
    isLoading,
  } = useSelectedCompany();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return companies;
    return companies.filter((c) => formatCompany(c).toLowerCase().includes(s));
  }, [companies, q]);

  const label = isLoading
    ? "Loading…"
    : company ? formatCompany(company) : "Select company…";

  if (showCompanyLabel && company) {
    return (
      <div className="tg-tool" title={label} style={{ cursor: "default" }}>
        <Building2 size={13} />
        <span className="tg-tool-value">{label}</span>
      </div>
    );
  }

  if (!showCompanySwitcher) return null;

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`tg-tool ${open ? "is-open" : ""}`}
        title={label}
      >
        <Building2 size={13} />
        <span className="tg-tool-value">{label}</span>
        <ChevronDown size={13} />
      </button>

      {open && (
        <div
          style={{
            position: "absolute", top: "calc(100% + 6px)", right: 0,
            width: 260, maxHeight: 360, overflowY: "auto",
            background: "var(--color-bg-surface)",
            border: "1px solid var(--color-border-subtle)",
            borderRadius: 8,
            boxShadow: "0 6px 24px rgba(0,0,0,0.12)",
            zIndex: 100,
            padding: 6,
          }}
        >
          <div style={{ position: "relative", marginBottom: 4 }}>
            <Search size={12} style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", opacity: 0.5 }} />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search companies…"
              style={{
                width: "100%", height: 30, paddingLeft: 26, paddingRight: 8,
                fontSize: 12, border: "1px solid var(--color-border-subtle)",
                background: "var(--color-bg-base)", borderRadius: 7,
                color: "var(--color-text-primary)", outline: "none",
              }}
            />
          </div>

          {filtered.length === 0 && (
            <div style={{ padding: "10px 8px", fontSize: 12, color: "var(--color-text-secondary)" }}>
              No companies match.
            </div>
          )}
          {filtered.map((c) => (
            <Row
              key={String(c._id)}
              label={formatCompany(c)}
              active={String(companyId) === String(c._id)}
              onClick={() => { setCompanyId(String(c._id)); setOpen(false); }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function Row({ label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "flex", alignItems: "center", gap: 8, width: "100%",
        padding: "8px 8px", border: "none",
        background: active ? "var(--crm-primary-soft)" : "transparent",
        color: "var(--color-text-primary)",
        cursor: "pointer", borderRadius: 6,
        textAlign: "left",
      }}
      onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = "var(--color-bg-base)"; }}
      onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "transparent"; }}
    >
      <Building2 size={13} style={{ color: "var(--crm-primary)", flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {label}
      </div>
      {active && <Check size={13} style={{ color: "var(--crm-primary)" }} />}
    </button>
  );
}
